<template>
  <v-form :action="url" ref="entrypoint" enctype="multipart/form-data" method="POST" v-on:submit.stop.prevent="submitForm">
    <v-container>
      <div class="alert-max-files" v-if="uploadMultiple && limiteMaximoAtingido">
        <i class="material-icons">error_outline</i>
        <span>
          {{ $t('message.max_arquivos_upload', { maxFiles: maxFiles }) }}
        </span>
      </div>
      <div name="dropzone" class="dropzone" :class="uploadMultiple ? 'layout-multiplo' : 'layout-unico'" :id="id" :numMaxArquivos="numMaxArquivos"></div>
      <div style="min-height: 20px;" >
        <v-scroll-y-transition >
          <span class="red--text caption" v-show="exibirMensagem">
            {{ $t('label.upload_arquivo_obrigatorio') }}
          </span>
        </v-scroll-y-transition>
      </div>
      <slot name="campos"></slot>
      <slot name="botoes">
        <v-row class="mt-6 mx-0">
          <v-spacer></v-spacer>
          <slot name="botaoSecundario"></slot>
          <v-btn
            v-if="showButtonSend"
            :disabled="!possuiArquivosValidos"
            type="submit"
            color="secondary"
          >
            {{ $t('label.enviar') }}
          </v-btn>
        </v-row>
      </slot>
    </v-container>
    <confirm
      ref="modalErrosUpload"
      :title="$t('label.atencao')"
      @agree="executarEnvio"
      :agree-label="$t('label.seguir')"
      :disagree-label="$t('label.voltar')"
      :message="mensagemConfirmacaoErrosUpload">
    </confirm>
  </v-form>
</template>

<script>
import { mapGetters } from 'vuex';
import { validAndRefreshToken } from '@/config/authKeycloak';
import { authIDMagalu } from '@/config/authIdMagalu';
import Dropzone from 'dropzone';
import Confirm from '../dialog/Confirm';

Dropzone.autoDiscover = false;

export default {
  components: {
    Confirm,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    url: {
      type: String,
      required: true,
    },
    numMaxArquivos: {
      type: Boolean,
      default: false,
    },
    clickable: {
      type: Boolean,
      default: true,
    },
    acceptedFileTypes: {
      type: String,
    },
    thumbnailHeight: {
      type: Number,
      default: 200,
    },
    thumbnailWidth: {
      type: Number,
      default: 200,
    },
    showRemoveLink: {
      type: Boolean,
      default: true,
    },
    maxFileSizeInMB: {
      type: Number,
      default: 10,
    },
    maxNumberOfFiles: {
      type: Number,
      default: 1,
    },
    autoProcessQueue: {
      type: Boolean,
      default: true,
    },
    dropzoneOptions: {
      type: Object,
      default() { return null; },
    },
    title: {
      type: String,
      default() { return 'Arraste o seu arquivo aqui'; },
    },
    timeout: {
      type: Number,
      default: 120000,
    },
    showButtonSend: {
      type: Boolean,
      default: true,
    },
    uploadMultiple: {
      type: Boolean,
      default: false,
    },
    customValidate: Function,
  },
  data() {
    return {
      dropzone: null,
      quantidadeArquivo: 0,
      exibirMensagem: false,
      mensagemConfirmacaoErrosUpload: '',
    };
  },
  computed: {
    ...mapGetters('roles', [
      'getTipoAuth',
    ]),
    addIcon() {
      return '<i class="material-icons">add</i>';
    },
    doneIcon() {
      return '<i class="material-icons">done</i>';
    },
    errorIcon() {
      return '<i class="material-icons">error</i>';
    },
    maxFiles() {
      return this.uploadMultiple ? 10 : this.maxNumberOfFiles;
    },
    possuiArquivos() {
      return this.quantidadeArquivo > 0;
    },
    possuiArquivosValidos() {
      return this.dropzone && this.dropzone.getQueuedFiles().length > 0;
    },
    limiteMaximoAtingido() {
      return this.quantidadeArquivo >= this.maxFiles;
    },
  },
  watch: {
    url() {
      this.recreateDropzone();
    },
    id() {
      this.recreateDropzone();
    },
    numMaxArquivos() {
      this.removeAllFiles();
    },
  },
  methods: {
    submitForm() {
      if (this.validateForm()) {
        if (this.uploadMultiple) {
          const arquivosComErro = this.dropzone.getRejectedFiles();
          const arquivosValidos = this.dropzone.getQueuedFiles();

          if (arquivosComErro.length > 0) {
            const dadosUpload = {
              quantidadeErros: arquivosComErro.length,
              quantidadeValidos: arquivosValidos.length,
            };
            this.tratarValidacaoErros(dadosUpload);
            return;
          }
        }

        this.executarEnvio();
      }
    },
    tratarValidacaoErros(dados) {
      if (dados.quantidadeValidos === 0) {
        this.$toast.error('Não existem arquivos válidos para serem enviados.');
        return;
      }
      const qtdErros = dados.quantidadeErros;
      if (qtdErros > 1) {
        this.mensagemConfirmacaoErrosUpload = this.$tc('message.upload_com_erro', 2, { qtdErros });
      } else {
        this.mensagemConfirmacaoErrosUpload = this.$tc('message.upload_com_erro', 1, { qtdErros });
      }
      this.$refs.modalErrosUpload.open();
    },
    executarEnvio() {
      const refreshTokenPromise = new Promise((resolve, reject) => {
        if (this.getTipoAuth === 'idMagalu') {
          authIDMagalu.setTokenInSession();
          resolve();
        } else {
          validAndRefreshToken().then(resolve).catch(reject);
        }
      });

      refreshTokenPromise.then(() => {
        const arquivosComErro = this.dropzone.getRejectedFiles();
        arquivosComErro.forEach((file) => this.dropzone.removeFile(file));

        this.dropzone.processQueue();
      }).catch((err) => {
        console.error('Falha ao atualizar token:', err);
      });
    },
    resetForm() {
      this.$refs.entrypoint.reset();
      this.quantidadeArquivo = 0;
      this.exibirMensagem = false;
      if (this.dropzone) {
        this.dropzone.removeAllFiles(true);
      }
    },
    resetValidationForm() {
      this.exibirMensagem = false;
      this.$refs.entrypoint.resetValidation();
    },
    validateForm() {
      if (this.quantidadeArquivo < 1) {
        this.exibirMensagem = true;
      } else {
        this.exibirMensagem = false;
      }
      return (!this.customValidate || this.customValidate())
        && this.$refs.entrypoint.validate() && !this.exibirMensagem;
    },
    removeAllFiles() {
      this.dropzone.removeAllFiles(true);
    },
    processQueue() {
      this.dropzone.processQueue();
    },
    recreateDropzone() {
      this.dropzone.destroy();
      this.dropzone = null;
      document.querySelector(`#${this.id}`).innerHTML = '';
      this.initDropzone();
    },
    createDropzoneElement() {
      let element = document.getElementById(this.id);
      if (!element) {
        element = this.$refs.entrypoint.querySelector('.dropzone');
      }
      return element;
    },
    createDropzone() {
      const element = this.createDropzoneElement();
      this.dropzone = this.getObjetoDropzone(element);
    },
    getObjetoDropzone(element) {
      if (this.dropzoneOptions) {
        return new Dropzone(element, this.dropzoneOptions);
      }
      if (this.uploadMultiple) {
        return this.criarDropzoneMultiple(element);
      }
      return this.criarDropzone(element);
    },
    criarDropzone(element) {
      return new Dropzone(element, {
        url: this.url,
        addRemoveLinks: this.showRemoveLink,
        acceptedFiles: this.acceptedFileTypes,
        autoProcessQueue: this.autoProcessQueue,
        clickable: this.clickable,
        dictCancelUpload: 'Cancelar',
        dictCancelUploadConfirmation: 'Tem certeza que deseja cancelar o envio?',
        dictDefaultMessage: `${this.title}<br>${this.addIcon}`,
        dictFallbackMessage: 'O seu browser não suporta este recurso. Use o Google Chrome.',
        dictFileTooBig: `O arquivo é muito grande! O máximo suportado é ${this.maxFileSizeInMB}MB`,
        dictInvalidFileType: `Este arquivo não é suportado. Os tipos aceitos são: ${this.acceptedFileTypes}`,
        dictMaxFilesExceeded: `O número máximo de arquivos para envio é: ${this.maxFiles}.`,
        dictResponseError: 'Erro no nosso servidor. Reenvie o arquivo. Status: {{statusCode}}',
        dictRemoveFile: 'Remover arquivo',
        maxFiles: this.maxFiles,
        maxFilesize: this.maxFileSizeInMB,
        timeout: this.timeout,
        accept: (file, done) => {
          done();
        },
        previewTemplate: `
            <div class="dz-preview dz-file-preview">
              <div class="dz-image" style="width: ${this.thumbnailWidth}px; height:${this.thumbnailHeight}px">
                <img data-dz-thumbnail />
              </div>
              <div class="dz-details">
                <div class="dz-size">
                  <span data-dz-size></span>
                </div>
                <div class="dz-filename">
                  <span data-dz-name></span>
                </div>
              </div>
              <div class="dz-progress">
                <span class="dz-upload" data-dz-uploadprogress></span>
              </div>
              <div class="dz-error-message">
                <span data-dz-errormessage></span>
              </div>
              <div class="dz-success-mark">${this.doneIcon}</div>
              <div class="dz-error-mark">${this.errorIcon}</div>
            </div>`,
        thumbnailWidth: this.thumbnailWidth,
        thumbnailHeight: this.thumbnailHeight,
      });
    },
    criarDropzoneMultiple(element) {
      return new Dropzone(element, {
        url: this.url,
        addRemoveLinks: false,
        acceptedFiles: this.acceptedFileTypes,
        autoProcessQueue: this.autoProcessQueue,
        uploadMultiple: true,
        parallelUploads: this.maxFiles,
        clickable: this.clickable,
        dictCancelUpload: 'Cancelar',
        dictCancelUploadConfirmation: 'Tem certeza que deseja cancelar o envio?',
        dictDefaultMessage: `
          <div class="dropzone-custom-placeholder">
            <span class="placeholder-title">Arraste seus arquivos aqui</span>
            <span class="placeholder-subtitle">Limite de ${this.maxFileSizeInMB}MB por arquivo e no máximo ${this.maxFiles} arquivos</span>
            <div class="placeholder-icon">+</div>
          </div>
        `,
        dictFallbackMessage: 'O seu browser não suporta este recurso. Use o Google Chrome.',
        dictFileTooBig: `O arquivo é muito grande! O máximo suportado é ${this.maxFileSizeInMB}MB`,
        dictInvalidFileType: `Este arquivo não é suportado. Os tipos aceitos são: ${this.acceptedFileTypes}`,
        dictMaxFilesExceeded: `O número máximo de arquivos para envio é: ${this.maxFiles}.`,
        dictResponseError: 'Erro no nosso servidor. Reenvie o arquivo. Status: {{statusCode}}',
        dictRemoveFile: 'Remover arquivo',
        maxFiles: this.maxFiles,
        maxFilesize: this.maxFileSizeInMB,
        timeout: this.timeout,
        accept: (file, done) => {
          // Verifica se já existe um arquivo com o mesmo nome na fila
          const arquivosDuplicados = this.dropzone.files.filter((f) => f.name === file.name);
          if (arquivosDuplicados.length > 1) {
            done(this.$t('message.arquivo_duplicado'));
          } else {
            done();
          }
        },
        previewTemplate: `
          <div class="dz-preview dz-file-preview custom-file-preview">
            <div class="file-info-row">
              <div class="file-name-group">
                <i class="material-icons error-icon" data-dz-errormark>error_outline</i>
                <span class="file-name" data-dz-name></span>
              </div>
              <div class="file-status-group">
                <span class="status-uploading">Enviando...</span>
                <span class="status-size" data-dz-size></span>
                <i class="material-icons remove-icon" data-dz-remove>delete_outline</i>
              </div>
            </div>

            <div class="dz-error-message-row">
                <span class="status-error" data-dz-errormessage></span>
            </div>

            <div class="file-progress-row">
              <div class="progress-track">
                <div class="progress-bar" data-dz-uploadprogress></div>
              </div>
            </div>
          </div>
        `,
      });
    },
    initDropzone() {
      this.createDropzone();
      this.registerDropzoneEvents();
    },
    removeFile(file) {
      this.dropzone.removeFile(file);
    },
    registerDropzoneEvents() {
      this.dropzone.on('drop', () => {
        if (this.maxFiles === 1) {
          this.removeAllFiles();
        }
      });
      this.dropzone.on('addedfile', (file) => {
        this.quantidadeArquivo += 1;
        this.exibirMensagem = false;
        this.$emit('UPLOADFILE_FILE_ADDED', file);
      });

      this.dropzone.on('maxfilesexceeded', (file) => {
        this.dropzone.removeFile(file);
      });

      this.dropzone.on('removedfile', (file) => {
        this.quantidadeArquivo -= 1;
        if (this.quantidadeArquivo === 0) {
          this.resetForm();
        }
        this.$emit('UPLOADFILE_FILE_REMOVED', file);
      });

      this.dropzone.on('success', (file, response) => {
        this.quantidadeArquivo -= 1;
        this.$emit('UPLOADFILE_SUCCESS', file, response);
        if (!this.uploadMultiple) {
          this.resetForm();
        }
      });

      this.dropzone.on('error', (file, message, xhr) => {
        let msgErro = message;

        if (typeof message === 'object') {
          msgErro = message.error || message.message || 'Erro interno no servidor';
        }

        const errorElement = file.previewElement.querySelector('[data-dz-errormessage]');
        if (errorElement) {
          errorElement.textContent = msgErro;
        }

        this.$emit('UPLOADFILE_ERROR', file, msgErro, xhr);
      });

      this.dropzone.on('sending', (file, xhr, formData) => {
        if (this.uploadMultiple) {
          return;
        }

        let token = '';
        if (sessionStorage && (sessionStorage.token || sessionStorage.id_magalu_access_token)) {
          token = sessionStorage.token || sessionStorage.id_magalu_access_token;
        }
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('x-tenant-id', sessionStorage.getItem('xTenantId'));

        const fn = encodeURI(file.name);
        formData.append('filename', fn);
        this.$emit('UPLOADFILE_SENDING', file, xhr, formData);
      });

      this.dropzone.on('successmultiple', (files, response) => {
        this.quantidadeArquivo = 0;
        this.$emit('UPLOADFILE_SUCCESS_MULTIPLE', files, response);
      });

      this.dropzone.on('sendingmultiple', (files, xhr, formData) => {
        let token = '';
        if (sessionStorage && (sessionStorage.token || sessionStorage.id_magalu_access_token)) {
          token = sessionStorage.token || sessionStorage.id_magalu_access_token;
        }
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('x-tenant-id', sessionStorage.getItem('xTenantId'));

        this.$emit('UPLOADFILE_SENDING_MULTIPLE', files, xhr, formData);
      });
    },
    startComponent() {
      this.initDropzone();
    },
  },
  mounted() {
    this.startComponent();
  },
};
</script>

<style lang="scss">
  @import './Dropzone.scss';
  @import './DropzoneMultiple.scss';
</style>
